class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.close();
      }
    });

    // Checkout button loading state. Delegated (the footer re-renders on cart
    // updates, so a direct listener would be lost). On the first click we show a
    // spinner and let the native submit proceed to /checkout; subsequent clicks
    // are swallowed so the ~1-2s redirect can't be re-triggered (the "re-click"
    // pattern from session recordings). We must NOT disable the button here —
    // disabling a submit button in its own click handler cancels the submit.
    this.addEventListener('click', (event) => {
      const checkoutBtn = event.target.closest('#CartDrawer-Checkout, .cart__checkout-button');
      if (!checkoutBtn || checkoutBtn.disabled) return;
      if (checkoutBtn.dataset.loading === 'true') {
        event.preventDefault();
        return;
      }
      checkoutBtn.dataset.loading = 'true';
      checkoutBtn.classList.add('cart__checkout-button--loading');
      checkoutBtn.setAttribute('aria-busy', 'true');
    });

    // Reset the loading state if the user returns via back/forward (bfcache),
    // otherwise the button would still show a spinner.
    window.addEventListener('pageshow', () => {
      const btn = this.querySelector('#CartDrawer-Checkout');
      if (!btn) return;
      btn.dataset.loading = 'false';
      btn.classList.remove('cart__checkout-button--loading');
    });

    // Initialize high-converting features
    this.initDiscountCodeHandler();
    this.initDeliveryGuarantee();
    this.startCountdownTimer();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  startCountdownTimer() {
    const timerEl = this.querySelector('#CartDrawerTimer');
    if (!timerEl) return;

    let timerEnd = sessionStorage.getItem('moksae_cart_timer_end');
    const DURATION = 10 * 60 * 1000; // 10 minutes in ms

    if (!timerEnd || Number(timerEnd) < Date.now()) {
      timerEnd = Date.now() + DURATION;
      sessionStorage.setItem('moksae_cart_timer_end', timerEnd);
    } else {
      timerEnd = Number(timerEnd);
    }

    if (this._timerInterval) clearInterval(this._timerInterval);

    const updateTimerDisplay = () => {
      const remainingMs = timerEnd - Date.now();
      if (remainingMs <= 0) {
        // Reset 10-minute timer on expiry
        timerEnd = Date.now() + DURATION;
        sessionStorage.setItem('moksae_cart_timer_end', timerEnd);
      }

      const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      const formatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      if (timerEl) timerEl.textContent = formatted;
    };

    updateTimerDisplay();
    this._timerInterval = setInterval(updateTimerDisplay, 1000);
  }

  initDeliveryGuarantee() {
    const dateEl = this.querySelector('#CartDrawerDeliveryDate');
    const timerEl = this.querySelector('#CartDrawerDeliveryTimer');
    if (!dateEl && !timerEl) return;

    const now = new Date();
    // Delivery 3-5 days out
    const deliveryDate = new Date(now);
    deliveryDate.setDate(now.getDate() + 4);

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[deliveryDate.getDay()];
    const monthName = months[deliveryDate.getMonth()];
    const dateNum = deliveryDate.getDate();

    if (dateEl) {
      dateEl.textContent = `${dayName} (${monthName} ${dateNum})`;
    }

    // Daily dispatch cut-off at 5:00 PM (17:00)
    const cutoff = new Date(now);
    cutoff.setHours(17, 0, 0, 0);

    if (now > cutoff) {
      cutoff.setDate(cutoff.getDate() + 1);
    }

    const diffMs = cutoff - now;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (timerEl) {
      timerEl.textContent = `${hours} h ${mins} min`;
    }
  }

  initDiscountCodeHandler() {
    const applyBtn = this.querySelector('#CartDrawer-DiscountApply');
    const input = this.querySelector('#CartDrawer-DiscountInput');
    const msgEl = this.querySelector('#CartDrawer-DiscountMessage');

    if (!applyBtn || !input) return;

    const handleApply = async () => {
      const code = input.value.trim();
      if (!code) {
        if (msgEl) {
          msgEl.textContent = 'Please enter a discount code';
          msgEl.className = 'cart-drawer__discount-message is-error';
          msgEl.style.display = 'block';
        }
        return;
      }

      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying...';
      if (msgEl) msgEl.style.display = 'none';

      try {
        // Apply discount code via Shopify endpoint
        const res = await fetch(`/discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (msgEl) {
          msgEl.textContent = `Discount '${code}' applied!`;
          msgEl.className = 'cart-drawer__discount-message is-success';
          msgEl.style.display = 'block';
        }

        // Re-render cart contents so discounts reflect
        if (window.CartRewards && typeof window.CartRewards.refreshCartDrawer === 'function') {
          await window.CartRewards.refreshCartDrawer();
        } else {
          location.reload();
        }
      } catch (err) {
        console.error('Failed to apply discount:', err);
        if (msgEl) {
          msgEl.textContent = 'Could not apply discount code';
          msgEl.className = 'cart-drawer__discount-message is-error';
          msgEl.style.display = 'block';
        }
      } finally {
        applyBtn.disabled = false;
        applyBtn.textContent = 'Apply';
      }
    };

    applyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      handleApply();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.startCountdownTimer();
    this.initDeliveryGuarantee();
    this.initDiscountCodeHandler();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Sync rewards on every open (e.g. a cart carried over from an earlier
    // visit). Called from here rather than patched on by cart-rewards.js, which
    // can load before this element is defined and then silently miss it.
    if (typeof window.scheduleRewardValidation === 'function') window.scheduleRewardValidation();
  }

  // Optimistic open: slide the drawer in with a spinner overlay the instant an
  // add-to-cart starts, instead of waiting ~1s for /cart/add to return the
  // rendered drawer. renderContents clears it; a failed add closes the drawer
  // so the page's own error message is visible. The timeout is a safety net.
  showPending() {
    this.classList.add('animate', 'active', 'cart-drawer--optimistic');
    document.body.classList.add('overflow-hidden');
    clearTimeout(this._pendingTimer);
    this._pendingTimer = setTimeout(() => this.clearPending(), 6000);
  }

  clearPending(failed = false) {
    clearTimeout(this._pendingTimer);
    if (!this.classList.contains('cart-drawer--optimistic')) return;
    this.classList.remove('cart-drawer--optimistic');
    if (failed) this.close();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement || null);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.clearPending();
    // renderContents only ever runs after an add — the cart is not empty.
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerHTML = parsedState.sections['cart-drawer'];
    const itemsTarget = this.querySelector('#CartDrawer-CartItems');
    const hadItemsList = !!(itemsTarget && itemsTarget.querySelector('.cart-items tbody'));

    if (drawerHTML && hadItemsList) {
      // Drawer is already showing items — update fragments in place so
      // unchanged product rows (and their images) don't flash on add.
      const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const itemsFresh = doc.querySelector('#CartDrawer-CartItems');
      if (itemsFresh) {
        if (window.morphCartItemsContents) {
          window.morphCartItemsContents(itemsTarget, itemsFresh.innerHTML);
        } else {
          itemsTarget.innerHTML = itemsFresh.innerHTML;
        }
      }
      // The reward bar is swapped so its fill slides from the old width.
      const barTarget = this.querySelector('#cart-reward-bar');
      const barFresh = doc.querySelector('#cart-reward-bar');
      if (barTarget && barFresh) {
        if (window.swapRewardBar) window.swapRewardBar(barTarget, barFresh);
        else barTarget.replaceWith(barFresh);
      }
      // These fragments genuinely change on add (totals, suggestions).
      ['.cart-drawer__footer', '.cart-drawer__upsell-section', '.cart-drawer__recommendations-section', '.cart-drawer__trust-section', '.cart-drawer__delivery-guarantee'].forEach(
        (selector) => {
          const target = this.querySelector(selector);
          const fresh = doc.querySelector(selector);
          if (target && fresh) target.replaceWith(fresh);
        }
      );
    } else if (drawerHTML) {
      // Empty → non-empty (or unexpected structure): swap the whole drawer.
      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl) cartDrawerEl.innerHTML = this.getSectionInnerHTML(drawerHTML, '#CartDrawer');
    }

    const bubbleHTML = parsedState.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) bubble.innerHTML = this.getSectionInnerHTML(bubbleHTML, '.shopify-section');

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Every add-to-cart on the site (PDPs, collection cards, UGC, landing pages,
// bundle builder) goes through fetch('/cart/add…'), so one hook here gives all
// of them the optimistic open. Skipped when the drawer is already open (e.g.
// in-drawer upsells) and for the reward system's own requests (X-Cart-Internal).
(() => {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const drawer = document.querySelector('cart-drawer');
      const internal = !!(init && init.headers && new Headers(init.headers).has('X-Cart-Internal'));
      if (
        method === 'POST' &&
        /\/cart\/add(\.js)?(\?|$)/.test(url) &&
        drawer &&
        typeof drawer.showPending === 'function' &&
        !internal &&
        !drawer.classList.contains('active')
      ) {
        drawer.showPending();
        const request = nativeFetch(input, init);
        request.then(
          (res) => {
            if (!res.ok) drawer.clearPending(true);
          },
          () => drawer.clearPending(true)
        );
        return request;
      }
    } catch (err) {
      // never let the hook break a cart request
    }
    return nativeFetch(input, init);
  };
})();

class CartDrawerItems extends CartItems {
  // On a quantity change, re-render only the fragments that actually change —
  // the item rows and the footer totals. Leaving the reward bar, upsell and
  // recommendations untouched avoids replaying their entry animations (the
  // cause of the per-click flicker) and cuts the DOM work. The reward bar is
  // updated smoothly in place by cart-rewards.js. (Adding a product from the
  // PDP still does a full drawer render via CartDrawer.renderContents.)
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
